import { createIrisQueue } from "api/account/client";
import Button from "components/components/Button/Button";
import {
  ButtonsContainer,
  ContentContainer,
} from "components/components/Iris/AuthenticatedView/IrisDetailsPage/IrisDetails/CreateQueueModal/style";
import { LoadingSpinnerContainer } from "components/components/Iris/commonStyle";
import LoadingSpinner from "components/components/LoadingSpinner/LoadingSpinner";
import Modal, { ModalSize } from "components/components/Modal/Modal";
import WarningContainer from "components/components/WarningContainer/WarningContainer";
import {
  RequestStatus,
  useRequestWithDefaultErrorHandling,
} from "hooks/useRequest";
import React, { useEffect, useRef } from "react";

import QueueTitleInput from "./QueueTitleInput";

type Props = {
  isActive: boolean;
  close: () => void;
  fetchIrisDetails: () => void;
  queueTitles: string[];
};

const CreateQueueModal: React.FC<Props> = ({
  isActive,
  close,
  fetchIrisDetails,
  queueTitles,
}) => {
  const [queueTitle, setQueueTitle] = React.useState("");
  const [hasError, setHasError] = React.useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else {
      setQueueTitle("");
    }
  }, [isActive]);

  const validateQueueTitle = (title: string): string | null => {
    const regex = /^[a-zA-Z0-9\s.,\-_()]+$/;

    if (!title.trim()) {
      return "Queue title is required.";
    }
    if (!/[a-zA-Z0-9]/.test(title)) {
      return "Queue title must contain at least one letter or number.";
    }
    if (!regex.test(title)) {
      return "Queue title contains invalid characters. Allowed characters are letters, numbers, spaces, and the following symbols: . , - _ ( )";
    }
    if (
      queueTitles.some(
        (existingTitle: string): boolean =>
          existingTitle.toLowerCase().trim() === title.toLowerCase().trim()
      )
    ) {
      return "Queue title must be unique.";
    }
    return null;
  };

  const { request: queueCreationRequest, status: requestStatus } =
    useRequestWithDefaultErrorHandling(async () => {
      await createIrisQueue(queueTitle);
    }, `Sorry, something went wrong when creating the queue.`);

  const createQueue = async (): Promise<void> => {
    if (queueTitle && !hasError) {
      await queueCreationRequest();
      close();
      fetchIrisDetails();
    }
  };

  return (
    <Modal size={ModalSize.medium} isActive={isActive} onClose={close}>
      <div aria-live="polite">
        {requestStatus === RequestStatus.IN_PROGRESS ? (
          <LoadingSpinnerContainer>
            <LoadingSpinner isLoading />
            <p>Creating queue...</p>
          </LoadingSpinnerContainer>
        ) : (
          <ContentContainer>
            <h2>Please create a queue title (required)</h2>
            <p>
              You can use queue titles to help you keep track of the purpose of
              each queue within your account.
            </p>
            <QueueTitleInput
              ref={inputRef}
              onChange={(event): void => setQueueTitle(event.target.value)}
              value={queueTitle}
              name="title"
              id="title"
              label="Queue title"
              placeholder="Type here"
              maxLength={100}
              validate={(title): string | null => {
                const error = validateQueueTitle(title);
                setHasError(!!error);
                return error;
              }}
            />
            <span> Characters remaining: {100 - queueTitle.length}</span>
            <div className="warning-container">
              <WarningContainer>
                <>
                  Once the queue is created,{" "}
                  <strong>you will not be able to change it.</strong>
                </>
              </WarningContainer>
            </div>

            <hr />
            <ButtonsContainer>
              <Button
                ariaLabel="Cancel"
                buttonText="Cancel"
                className="cancel"
                onClick={close}
              />
              <Button
                ariaLabel={`Create queue${
                  hasError || !queueTitle ? " (disabled)" : ""
                }`}
                buttonText="Create"
                onClick={createQueue}
                disabled={hasError || !queueTitle}
              />
            </ButtonsContainer>
          </ContentContainer>
        )}
      </div>
    </Modal>
  );
};

export default CreateQueueModal;
